/**
 * executiveDataConfidence.js
 * ─────────────────────────────────────────────────────────────
 * "Report Confidence" assessment for the Executive Report.
 *
 * This module's ONLY responsibility is to validate the reliability and
 * completeness of data that has already been computed elsewhere. It does
 * NOT perform business analysis, does NOT judge project performance, and
 * NEVER influences any financial calculation. It only communicates how
 * much confidence an executive should place in the numbers already shown
 * in the report.
 *
 * Design constraints (per explicit product decision):
 *   - Every check is a strict logical/mathematical consistency check, or a
 *     count/percentage of missing data — never a business-assumption
 *     threshold ("more than X% is bad") that would be valid for one
 *     project but not another.
 *   - No database queries. This function takes the already-computed
 *     outputs of buildCumulativeData() (via the `packages` list built by
 *     ExecutiveReport.jsx) and the already-fetched risk rows — nothing
 *     here re-fetches or recalculates anything.
 *   - No new financial calculations. Checks only compare fields that
 *     already exist on each row (bac, eac, totalActualCost/actual,
 *     financial_impact, owner) — no new formula is introduced.
 *   - Fully generic: works identically for any CostSpace project, any
 *     number of packages, any number of risks — nothing here is
 *     project-specific or hardcoded to this project's data shape.
 *
 * Severity levels:
 *   - critical: an absolute logical/mathematical impossibility or
 *     contradiction (e.g. Gross Profit exceeding Contract Value — cannot
 *     happen under any legitimate business scenario).
 *   - warning: missing data that reduces the reliability of a downstream
 *     calculation or ranking (e.g. a risk with no financial_impact will
 *     always sort last under Top Risks' financial-exposure ranking, even
 *     if it's actually significant).
 *   - info: an objective, non-judgmental count or percentage — e.g.
 *     Forecast coverage. This is not "good" or "bad"; it is a fact about
 *     the data that the reader interprets themselves.
 */

/**
 * @param {Array} packages - the same `packages` array ExecutiveReport.jsx
 *   already builds from cumulative.directRows/indirectRows (each item has
 *   at minimum: name, budget, actual, eac).
 * @param {number} grossProfit - meta.grossProfit, already computed.
 * @param {number} contractValue - meta.contractValue, already fetched.
 * @param {Array} riskRows - the raw risk_register rows already fetched by
 *   ExecutiveReport.jsx's fetchAll() (before any .slice()).
 * @returns {{
 *   critical: Array<{ message: string, count: number }>,
 *   warnings: Array<{ message: string, count: number }>,
 *   forecastCoverage: { pct: number, withForecast: number, withoutForecast: number, total: number },
 *   isFullyConfident: boolean,
 *   overallStatus: 'full' | 'moderate' | 'low',
 * }}
 */
export function assessDataConfidence(packages, grossProfit, contractValue, riskRows) {
  const critical = []
  const warnings = []

  // ── Critical: Actual Cost > 0 while EAC = 0 ──
  // A logical contradiction — money has genuinely been spent, but no final
  // cost estimate exists for that package at all. Any VAC/CPI shown for
  // these packages is not yet meaningful.
  const actualWithNoEac = (packages || []).filter(p => (p.actual || 0) > 0 && (p.eac || 0) === 0)
  if (actualWithNoEac.length > 0) {
    critical.push({
      message: `${actualWithNoEac.length} package(s) have Actual Cost but no Forecast (EAC).`,
      count: actualWithNoEac.length,
    })
  }

  // ── Critical: Gross Profit > Contract Value ──
  // Mathematically impossible under any legitimate scenario (would require
  // a negative EAC). Signals a data error upstream (e.g. Contract Value or
  // EAC entered incorrectly), not a real business condition.
  if (contractValue && grossProfit > contractValue) {
    critical.push({
      message: `Gross Profit exceeds Contract Value — mathematically inconsistent.`,
      count: 1,
    })
  }

  // ── Warning: Risks without Financial Impact ──
  // These will always rank last under Top Risks' financial-exposure sort,
  // regardless of their actual severity, because there's nothing to sort by.
  const risksNoImpact = (riskRows || []).filter(r => !r.financial_impact)
  if (risksNoImpact.length > 0) {
    warnings.push({
      message: `${risksNoImpact.length} open risk(s) have no Financial Impact.`,
      count: risksNoImpact.length,
    })
  }

  // ── Warning: Risks without Owner ──
  // A risk with no accountable owner is unlikely to be actively tracked.
  const risksNoOwner = (riskRows || []).filter(r => !r.owner)
  if (risksNoOwner.length > 0) {
    warnings.push({
      message: `${risksNoOwner.length} open risk(s) have no assigned Owner.`,
      count: risksNoOwner.length,
    })
  }

  // ── Info: Forecast coverage — an objective fact, not a judgment ──
  const total = (packages || []).length
  const withForecast = (packages || []).filter(p => (p.eac || 0) > 0).length
  const withoutForecast = total - withForecast
  const pct = total > 0 ? (withForecast / total) * 100 : 0

  const isFullyConfident = critical.length === 0 && warnings.length === 0

  // Overall status — derived ONLY from whether critical/warning findings
  // exist at all, never from a count threshold (e.g. NOT "critical if more
  // than 3 issues"). Any critical finding, regardless of how many, means
  // the report contains at least one logical/mathematical inconsistency,
  // so status is 'low'. Zero critical but at least one warning means the
  // data is internally consistent but incomplete somewhere, so 'moderate'.
  // Zero of either means 'full'. This makes the status generic and
  // reusable across any project — it reacts only to the presence of
  // findings, never to their quantity.
  const overallStatus = critical.length > 0 ? 'low' : (warnings.length > 0 ? 'moderate' : 'full')

  return {
    critical,
    warnings,
    forecastCoverage: { pct, withForecast, withoutForecast, total },
    isFullyConfident,
    overallStatus,
  }
}
